interface TextPoint {
  node: Text
  offset: number
}

const WHITESPACE_PATTERN = /\s/

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const stripTruncationSuffix = (value: string): string => {
  if (value.endsWith('...')) return value.slice(0, -3)
  if (value.endsWith('\u2026')) return value.slice(0, -1)
  return value
}

/**
 * Finds the first occurrence of `needle` in the rendered text beneath `root`
 * and returns a DOM Range spanning the matching text nodes.
 *
 * Both strings are compared after collapsing each whitespace run to one space.
 * Whitespace in the normalized needle is then allowed to match zero or more
 * normalized haystack spaces. This matters for restored selections because
 * `Selection.toString()` inserts newlines at some rendered block boundaries,
 * even though concatenating those blocks' text nodes contributes no character
 * at all. The index maps below keep the original DOM start and end of every
 * normalized character, including the full extent of leading, trailing, and
 * cross-node whitespace runs, so the returned Range still covers the source
 * text rather than a rewritten string.
 *
 * Captured text is clamped with a trailing ellipsis, so one terminal `...` or
 * U+2026 is removed before searching and the available prefix can still be
 * restored. Matching is case-sensitive and stops at the first occurrence. An
 * empty needle, a whitespace-only needle, or an absent match returns no ranges.
 */
export const findNormalizedTextRanges = (
  root: Node,
  needle: string,
): Range[] => {
  const normalizedNeedle = stripTruncationSuffix(needle).replace(/\s+/g, ' ')
  if (normalizedNeedle.trim().length === 0) return []

  const ownerDocument = root.ownerDocument ?? (root as Document)
  const normalizedCharacters: string[] = []
  const starts: TextPoint[] = []
  const ends: TextPoint[] = []
  let previousWasWhitespace = false

  const appendTextNode = (node: Text) => {
    for (let offset = 0; offset < node.data.length; offset += 1) {
      const character = node.data[offset]
      if (WHITESPACE_PATTERN.test(character)) {
        if (previousWasWhitespace) {
          // One normalized space can span multiple source characters and text
          // nodes. Extend its end point so a matching Range covers all of it.
          ends[ends.length - 1] = { node, offset: offset + 1 }
        } else {
          normalizedCharacters.push(' ')
          starts.push({ node, offset })
          ends.push({ node, offset: offset + 1 })
          previousWasWhitespace = true
        }
        continue
      }

      normalizedCharacters.push(character)
      starts.push({ node, offset })
      ends.push({ node, offset: offset + 1 })
      previousWasWhitespace = false
    }
  }

  if (root.nodeType === Node.TEXT_NODE) {
    appendTextNode(root as Text)
  } else {
    const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    for (
      let current = walker.nextNode();
      current !== null;
      current = walker.nextNode()
    ) {
      appendTextNode(current as Text)
    }
  }

  const normalizedHaystack = normalizedCharacters.join('')
  const pattern = normalizedNeedle.split(' ').map(escapeRegExp).join(' *')
  let match: RegExpExecArray | null
  try {
    match = new RegExp(pattern).exec(normalizedHaystack)
  } catch {
    // Treat regex-engine limits on pathological captured text as no match
    // instead of breaking file navigation.
    return []
  }
  if (!match || match[0].length === 0) return []

  const start = starts[match.index]
  const end = ends[match.index + match[0].length - 1]
  if (!start || !end) return []

  const range = ownerDocument.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  return [range]
}
